import { useState } from 'react';
import type { FormEvent } from 'react';
import { requestPasswordReset, resetPassword } from './api';
import { navigate, paths } from './router';
import { Banner, Button, LogoLockup, TextInput } from './ui';

/**
 * Mot de passe oublié (US-53).
 *
 * Un seul écran pour les deux moitiés du parcours, distinguées par la présence
 * du jeton dans l'URL. Deux écrans auraient partagé la même mise en page, le
 * même titre et la même sortie, pour la seule satisfaction d'avoir deux
 * fichiers.
 *
 * **Ce que la demande répond ne dit jamais si le compte existe.** Le message de
 * confirmation est donc rédigé au conditionnel — « si un compte existe » — et
 * ce n'est pas une précaution de langage : c'est la seule formulation qui
 * corresponde à ce que le serveur a réellement fait.
 */
export interface ResetPasswordProps {
  /** Jeton lu dans l'URL, `null` sur la demande. */
  jeton: string | null;
}

export function ResetPassword({ jeton }: ResetPasswordProps): React.JSX.Element {
  return (
    /* Même composition que la connexion : c'est la même situation, et deux
       largeurs différentes pour deux écrans qui se suivent se remarquent.
       `max-w-6xl` posait ici une carte de 384 px au milieu de 1152. */
    <main className="mx-auto flex min-h-full w-full max-w-form flex-col justify-center gap-6 px-6 py-12">
      <div className="flex w-full flex-col gap-5">
        <h1 className="mb-2 flex justify-center">
          <LogoLockup />
        </h1>
        {jeton === null ? <Demande /> : <NouveauMotDePasse jeton={jeton} />}
      </div>
    </main>
  );
}

function Demande(): React.JSX.Element {
  const [email, setEmail] = useState('');
  const [envoye, setEnvoye] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (enCours) return;
    setEnCours(true);
    setError(null);
    requestPasswordReset(email)
      .then(() => { setEnvoye(true); })
      .catch((e: unknown) => { setError(e instanceof Error ? e.message : String(e)); })
      .finally(() => { setEnCours(false); });
  }

  if (envoye) {
    return (
      <>
        {/* Au conditionnel, parce que c'est exactement ce que le serveur a
            fait : il n'a pas dit si le compte existait, et l'écran ne peut
            donc pas l'affirmer non plus. */}
        <Banner tone="success">
          Si un compte existe pour cette adresse, un message vient d’y être envoyé. Le lien qu’il contient est
          valable une heure et ne fonctionne qu’une fois.
        </Banner>
        <Button type="button" variant="secondary" onClick={() => { navigate(paths.library); }}>
          Revenir à la connexion
        </Button>
      </>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <p className="text-center text-sm text-foreground-secondary">
        Entrez votre adresse : nous vous enverrons un lien pour choisir un nouveau mot de passe.
      </p>
      {error !== null && <Banner tone="danger">{error}</Banner>}
      <label className="flex flex-col gap-1 text-sm">
        Adresse e-mail
        <TextInput
          type="email"
          inputMode="email"
          autoComplete="username"
          value={email}
          onChange={(e) => { setEmail(e.target.value); }}
        />
      </label>
      <Button type="submit" variant="primary" disabled={enCours || email.trim() === ''}>
        Envoyer le lien
      </Button>
      <Button type="button" variant="ghost" onClick={() => { navigate(paths.library); }}>
        Revenir à la connexion
      </Button>
    </form>
  );
}

function NouveauMotDePasse({ jeton }: { jeton: string }): React.JSX.Element {
  const [password, setPassword] = useState('');
  const [fait, setFait] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (enCours) return;
    setEnCours(true);
    setError(null);
    resetPassword(jeton, password)
      .then(() => { setPassword(''); setFait(true); })
      .catch((e: unknown) => { setError(e instanceof Error ? e.message : String(e)); })
      .finally(() => { setEnCours(false); });
  }

  if (fait) {
    return (
      <>
        <Banner tone="success">
          Votre mot de passe est changé. Vos autres appareils ont été déconnectés.
        </Banner>
        <Button type="button" variant="primary" onClick={() => { navigate(paths.library); }}>
          Se connecter
        </Button>
      </>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <p className="text-center text-sm text-foreground-secondary">Choisissez un nouveau mot de passe.</p>
      {error !== null && <Banner tone="danger">{error}</Banner>}
      <label className="flex flex-col gap-1 text-sm">
        Nouveau mot de passe
        <TextInput
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={(e) => { setPassword(e.target.value); }}
        />
      </label>
      {/* Dit avant, pas après : se faire déconnecter partout sans avertissement
          ressemble à une panne. */}
      <p className="text-xs text-foreground-secondary">
        Huit caractères au minimum. Poser un nouveau mot de passe déconnecte tous vos appareils.
      </p>
      <Button type="submit" variant="primary" disabled={enCours || password.length < 8}>
        Enregistrer
      </Button>
    </form>
  );
}
